import { queryOptions } from "@tanstack/react-query";
import { unwrap, type components } from "@wilco/api";
import { api } from "../auth.ts";
import { idempotency } from "../docs/DocumentForm.tsx";

/*
 * Банк и «Витрати» на REST из contracts/openapi.json (модули banking и
 * opex, PR #58). Типы экрана это схемы контракта как есть: названия
 * контрагентов и статей сервер присылает сам, справочник не нужен.
 */
type S = components["schemas"];

export type Account = S["BankAccountCard"];
export type Transaction = S["BankTransactionCard"];
export type ImportResult = S["BankImportResult"];
export type SupplierOrder = S["BankSupplierOrder"];
export type Article = S["OpexArticleCard"];
export type Entry = S["OpexEntryCard"];
export type Rule = S["OpexRuleCard"];
export type Template = S["OpexTemplateCard"];
export type ManualEntry = S["OpexManualInput"];
export type RuleInput = S["OpexRuleInput"];
export type TemplateInput = S["OpexTemplateInput"];
export type AccountInput = S["BankAccountInput"];
export type AccountUpdate = S["BankAccountUpdate"];
export type Statement = S["BankStatementCard"];
export type ArticleUpdate = S["OpexArticleUpdate"];
export type ArticleInput = S["OpexArticleInput"];
export type Origin = "BANK" | "EXPENSE" | "MANUAL" | "TEMPLATE";

export type TxState = "PENDING" | "SUPPLIER_PAYMENT" | "CUSTOMER_RECEIPT" | "OPEX" | "OWN_TRANSFER" | "IGNORED";
export type StatementFormat = "CSV" | "AUTOCLIENT_JSON";

const id = (v: number) => ({ params: { path: { id: v } } });

/**
 * Списки сервер отдаёт страницами `{ items, next }`. Экраны банка пока
 * берут первую страницу наибольшего размера: счетов, статей и правил единицы,
 * а «Не зіставлено» разбирают сверху. Листание придёт с каркасом списков.
 */
const LIMIT = 200;
const items = <T>(page: Promise<{ items: T[] }>): Promise<T[]> => page.then((p) => p.items);

/** Файл выписки по расширению: .json это выгрузка Автоклієнта, остальное CSV. */
export const formatOf = (fileName: string): StatementFormat => (fileName.toLowerCase().endsWith(".json") ? "AUTOCLIENT_JSON" : "CSV");

export const moneyApi = {
  accounts: () => items(unwrap(api.GET("/api/v1/banking/accounts", { params: { query: { limit: LIMIT } } }))),
  addAccount: (body: AccountInput) => unwrap(api.POST("/api/v1/banking/accounts", { body })),
  transactions: (account: number) => items(unwrap(api.GET("/api/v1/banking/transactions", { params: { query: { account, limit: LIMIT } } }))),
  /** Тело это сам файл: CSV текстом, JSON Автоклієнта как есть. */
  importStatement: (account: number, format: StatementFormat, content: string) =>
    unwrap(
      api.POST("/api/v1/banking/accounts/{id}/statement", {
        params: { path: { id: account }, query: { format } },
        body: content,
        bodySerializer: (b: string) => b,
        headers: { "Content-Type": format === "CSV" ? "text/csv;charset=UTF-8" : "application/json" },
      }),
    ),
  sync: (account: number, from: string, to: string) =>
    unwrap(api.POST("/api/v1/banking/accounts/{id}/sync", { params: { path: { id: account }, query: { from, to } } })),
  pending: () => items(unwrap(api.GET("/api/v1/banking/transactions", { params: { query: { state: ["PENDING"], limit: LIMIT } } }))),
  supplierOrders: () => items(unwrap(api.GET("/api/v1/banking/supplier-orders", { params: { query: { limit: LIMIT } } }))),
  matchSupplier: (tx: number, order: string) => unwrap(api.POST("/api/v1/banking/transactions/{id}/match-supplier", { ...id(tx), body: { order } })),
  allocate: (tx: number, body: S["BankAllocateInput"]) => unwrap(api.POST("/api/v1/banking/transactions/{id}/allocate", { ...id(tx), body })),
  ignore: (tx: number, reason: string) => unwrap(api.POST("/api/v1/banking/transactions/{id}/ignore", { ...id(tx), body: { reason } })),

  articles: () => items(unwrap(api.GET("/api/v1/opex/articles", { params: { query: { limit: LIMIT } } }))),
  entries: (from: string, to: string) => items(unwrap(api.GET("/api/v1/opex/entries", { params: { query: { from, to, limit: LIMIT } } }))),
  addEntry: (body: ManualEntry) => unwrap(api.POST("/api/v1/opex/entries", { body })),
  rules: () => items(unwrap(api.GET("/api/v1/opex/rules", { params: { query: { limit: LIMIT } } }))),
  addRule: (body: RuleInput) => unwrap(api.POST("/api/v1/opex/rules", { body })),
  deleteRule: (rule: number) => unwrap(api.DELETE("/api/v1/opex/rules/{id}", id(rule))),
  templates: () => items(unwrap(api.GET("/api/v1/opex/templates", { params: { query: { limit: LIMIT } } }))),
  addTemplate: (body: TemplateInput) => unwrap(api.POST("/api/v1/opex/templates", { body })),
  stopTemplate: (template: number) => unwrap(api.POST("/api/v1/opex/templates/{id}/stop", id(template))),
};

/** Страница списка сервера: курсор как есть, фильтры параметрами (CursorPage). */
interface PageArgs {
  cursor: string | null;
  limit: number;
}
const paging = ({ cursor, limit }: PageArgs) => ({ limit, ...(cursor ? { cursor } : {}) });
const key = (k: string) => ({ headers: idempotency(k) });

/** Страница сервера в виде списка каркаса: next без значения это последняя страница. */
const asPage = <T>(p: Promise<{ items: T[]; next?: string | null }>) => p.then((r) => ({ items: r.items, next: r.next ?? null }));

/** Списки «Операцій» страницами и карточки документов банка и «Витрат». */
export const docsApi = {
  accounts: (q: string, p: PageArgs) => asPage(unwrap(api.GET("/api/v1/banking/accounts", { params: { query: { ...paging(p), ...(q ? { q } : {}) } } }))),
  account: (account: number) => unwrap(api.GET("/api/v1/banking/accounts/{id}", id(account))),
  createAccount: (body: AccountInput, k: string) => unwrap(api.POST("/api/v1/banking/accounts", { body, ...key(k) })),
  updateAccount: (account: number, body: AccountUpdate, k: string) => unwrap(api.PUT("/api/v1/banking/accounts/{id}", { ...id(account), body, ...key(k) })),
  payments: (q: string, state: string | null, p: PageArgs, statement?: number) =>
    asPage(
      unwrap(
        api.GET("/api/v1/banking/transactions", {
          params: { query: { ...paging(p), ...(q ? { q } : {}), ...(state ? { state: [state as TxState] } : {}), ...(statement ? { statement } : {}) } },
        }),
      ),
    ),
  payment: (tx: number) => unwrap(api.GET("/api/v1/banking/transactions/{id}", id(tx))),
  statements: (account: number | null, p: PageArgs) =>
    asPage(unwrap(api.GET("/api/v1/banking/statements", { params: { query: { ...paging(p), ...(account ? { account } : {}) } } }))),
  statement: (statement: number) => unwrap(api.GET("/api/v1/banking/statements/{id}", id(statement))),

  entries: (q: string, origin: string | null, p: PageArgs) =>
    asPage(unwrap(api.GET("/api/v1/opex/entries", { params: { query: { ...paging(p), ...(q ? { q } : {}), ...(origin ? { origin: origin as Origin } : {}) } } }))),
  entry: (entry: number) => unwrap(api.GET("/api/v1/opex/entries/{id}", id(entry))),
  createEntry: (body: ManualEntry, k: string) => unwrap(api.POST("/api/v1/opex/entries", { body, ...key(k) })),
  articles: (q: string, archived: boolean, p: PageArgs) =>
    asPage(unwrap(api.GET("/api/v1/opex/articles", { params: { query: { ...paging(p), archived, ...(q ? { q } : {}) } } }))),
  createArticle: (body: ArticleInput, k: string) => unwrap(api.POST("/api/v1/opex/articles", { body, ...key(k) })),
  updateArticle: (code: string, body: ArticleUpdate, k: string) => unwrap(api.PUT("/api/v1/opex/articles/{code}", { params: { path: { code } }, body, ...key(k) })),
  createRule: (body: RuleInput, k: string) => unwrap(api.POST("/api/v1/opex/rules", { body, ...key(k) })),
  updateRule: (rule: number, body: RuleInput, k: string) => unwrap(api.PUT("/api/v1/opex/rules/{id}", { ...id(rule), body, ...key(k) })),
  templates: (active: boolean | null, p: PageArgs) =>
    asPage(unwrap(api.GET("/api/v1/opex/templates", { params: { query: { ...paging(p), ...(active === null ? {} : { active }) } } }))),
  createTemplate: (body: TemplateInput, k: string) => unwrap(api.POST("/api/v1/opex/templates", { body, ...key(k) })),
  updateTemplate: (template: number, body: TemplateInput, k: string) => unwrap(api.PUT("/api/v1/opex/templates/{id}", { ...id(template), body, ...key(k) })),
};

// Ключи банка и «Витрат» начинаются с "money": разбор платежа меняет и
// очередь, и счёт, и реестр, экран сбрасывает их разом.
export const MONEY = ["money"] as const;

export const accountsQuery = queryOptions({ queryKey: [...MONEY, "accounts"], queryFn: moneyApi.accounts });
export const pendingQuery = queryOptions({ queryKey: [...MONEY, "pending"], queryFn: moneyApi.pending });
export const supplierOrdersQuery = queryOptions({ queryKey: [...MONEY, "supplier-orders"], queryFn: moneyApi.supplierOrders });
export const articlesQuery = queryOptions({ queryKey: [...MONEY, "articles"], queryFn: moneyApi.articles, staleTime: 5 * 60_000 });
export const rulesQuery = queryOptions({ queryKey: [...MONEY, "rules"], queryFn: moneyApi.rules });
export const templatesQuery = queryOptions({ queryKey: [...MONEY, "templates"], queryFn: moneyApi.templates });

export const transactionsQuery = (account: number) =>
  queryOptions({ queryKey: [...MONEY, "transactions", account], queryFn: () => moneyApi.transactions(account) });

/** Записи месяца YYYY-MM: [первое число, первое число следующего). */
export const entriesQuery = (month: string) =>
  queryOptions({ queryKey: [...MONEY, "entries", month], queryFn: () => moneyApi.entries(`${month}-01`, nextMonth(month)) });

export function nextMonth(month: string): string {
  const [y, m] = month.split("-").map(Number) as [number, number];
  const next = m === 12 ? { y: y + 1, m: 1 } : { y, m: m + 1 };
  return `${String(next.y)}-${String(next.m).padStart(2, "0")}-01`;
}

/** Сегодня по часам браузера: дата документа, а не момент. */
export function today(): string {
  const d = new Date();
  return `${String(d.getFullYear())}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
